import { useWalkthroughController } from '@/composables/useWalkthroughController';

/**
 * Normalizes an announcement's steps so every step carries its own `route`/`setup` — the
 * shape `useWalkthroughController.js`'s `start()` expects (see ADR 0006). Only the first step
 * inherits the announcement-level `route`/`setup`, matching what used to be a one-time
 * pre-flight before the Controller ever started: every existing single-page announcement
 * declares `route`/`setup` once, meant to run exactly once, not on every step. A step that
 * already declares its own `route`/`setup` (a multi-page onboarding walkthrough) is left
 * untouched.
 */
const normalizeSteps = (announcement) =>
  (announcement.steps ?? []).map((step, index) => {
    if (index !== 0) {
      return step;
    }

    return {
      ...step,
      route: step.route ?? announcement.route,
      setup: step.setup ?? announcement.setup,
    };
  });

/**
 * Starts a Guided Walkthrough for an announcement, from wherever the carrier happens to
 * be. Shared by every entry point (the What's New panel's card CTA, and the login modal's
 * once it's wired) so the sequence can't drift between them.
 *
 * The actual navigate → setup → wait-for-visible sequence per step now lives in
 * `useWalkthroughController.js`'s `start()`/`advance()`/`back()` — this composable's job is
 * just normalizing the announcement into the Controller's step shape and wiring analytics.
 */
export const useWalkthroughLaunch = () => {
  const walkthroughController = useWalkthroughController();
  const { trackEvent, eventNames } = useAcvAnalytics();

  const launchWalkthrough = async (announcement, { source } = {}) => {
    const steps = normalizeSteps(announcement);
    if (!steps.length) {
      return;
    }

    trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_STARTED, {
      announcement_id: announcement.id,
      announcement_name: announcement.name,
      source,
    });

    await walkthroughController.start(steps, {
      onComplete: ({ finalStepIndex, totalSteps }) => {
        trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_COMPLETED, {
          announcement_id: announcement.id,
          announcement_name: announcement.name,
          final_step_index: finalStepIndex,
          total_steps: totalSteps,
        });
      },
      onCancel: ({ finalStepIndex, totalSteps }) => {
        trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_CANCELLED, {
          announcement_id: announcement.id,
          announcement_name: announcement.name,
          final_step_index: finalStepIndex,
          total_steps: totalSteps,
        });
      },
    });
  };

  return { launchWalkthrough };
};
